import { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldAlert } from 'lucide-react';

import { getConcern, listConcerns } from '../../services/projectRegisterApi';
import RiskDetailPanel from './RiskDetailPanel';
import {
  Chip,
  DrawerDetail,
  EmptyState,
  ErrorBanner,
  FilterBar,
  FilterSelect,
  RISK_TYPE_TONE,
  T,
  concernStatusLabel,
  humanise,
  isClosed,
  personLabel,
  shortRef,
  statusTone,
  useDetailNav,
  Spinner,
} from './shared';

const ConcernRow = ({ concern, selected, onSelect }) => {
  const attention = !!concern.ambiguity?.blocking;
  const st = statusTone(concernStatusLabel(concern));
  const riskTone = RISK_TYPE_TONE[concern.risk_type] || {
    fg: T.textSec,
    bg: 'rgba(209,209,199,0.08)',
  };
  const closed = isClosed(concern);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-2xl p-3 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/40 ${
        selected
          ? 'border border-indigo-500/60 bg-indigo-950/20 shadow-md shadow-indigo-950/30 ring-1 ring-indigo-500/50'
          : 'border border-zinc-800/80 bg-zinc-900/80 shadow-sm hover:border-zinc-700 hover:bg-zinc-800/60'
      }`}
      style={{ opacity: closed ? 0.6 : 1 }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium text-zinc-400">
              {shortRef(concern.entity_id)}
            </span>
            <Chip fg={riskTone.fg} bg={riskTone.bg}>
              {concern.risk_type || humanise(concern.subtype)}
            </Chip>
          </div>
          <div className="mt-2 truncate text-[13px] font-medium text-zinc-100">
            {concern.description || shortRef(concern.entity_id)}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-400">
            <span className="font-medium text-zinc-300">
              {personLabel(concern.responsible_person_id) || 'Unassigned'}
            </span>
            {concern.linked_deliverable_id && (
              <>
                <span>·</span>
                <span className="text-zinc-400">
                  {concern.linked_deliverable_id}
                </span>
              </>
            )}
          </div>
          {attention && (
            <div className="mt-2.5">
              <Chip fg={T.decision} bg={T.decisionBg}>
                Needs a decision
              </Chip>
            </div>
          )}
        </div>
        <Chip fg={st.fg} bg={st.bg}>
          {st.label}
        </Chip>
      </div>
    </button>
  );
};

/**
 * Concerns tab: `subtype="RISK"` for Risks, `subtype="DEPENDENCIES"` for the
 * combined Dependencies + Blockers tab (see the backend's `DEPENDENCIES`
 * alias in `projectRegisterRouter.list_concerns`).
 */
const ConcernList = ({ accessToken, subtype }) => {
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showClosed, setShowClosed] = useState('OPEN');
  const {
    selectedId,
    detail,
    setDetail,
    loading: detailLoading,
    error: detailError,
    open: openDetail,
    close: closeDetail,
  } = useDetailNav((entityId) => getConcern(accessToken, entityId));

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const response = await listConcerns(accessToken, { subtype });
      setConcerns(response?.concerns || []);
    } catch (loadError) {
      setError(String(loadError?.message || loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken, subtype]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleActed = (updatedConcern) => {
    setConcerns((current) =>
      current.map((item) =>
        item.entity_id === updatedConcern.entity_id ? updatedConcern : item,
      ),
    );
    setDetail((current) =>
      current ? { ...current, concern: updatedConcern } : current,
    );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return concerns.filter((concern) => {
      if (showClosed === 'OPEN' && isClosed(concern)) return false;
      if (!q) return true;
      const haystack = [
        concern.description,
        concern.entity_id,
        concern.responsible_person_id,
        concern.linked_deliverable_id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [concerns, search, showClosed]);

  if (loading) return <Spinner />;

  const open = filtered.filter((concern) => !isClosed(concern));
  const closed = filtered.filter(isClosed);
  const label = subtype === 'RISK' ? 'risk' : 'dependency';
  const attentionCount = filtered.filter(
    (concern) => concern.ambiguity?.blocking,
  ).length;

  return (
    // h-full min-h-0 + shrink-0 filter row, same fixed-header/scrolling-body
    // split as CommitmentsList/InsightsGantt -- only the section list below
    // scrolls, the search/filter bar above it stays put.
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex shrink-0 flex-col gap-2">
        <ErrorBanner>{error}</ErrorBanner>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder={`Search ${label === 'risk' ? 'risks' : 'dependencies'}`}
        >
          <FilterSelect
            value={showClosed}
            onChange={(event) => setShowClosed(event.target.value)}
          >
            <option value="OPEN">Open only</option>
            <option value="ALL">Include closed</option>
          </FilterSelect>
        </FilterBar>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {filtered.length === 0 ? (
          <EmptyState icon={ShieldAlert}>
            No {label === 'risk' ? 'risks' : 'dependencies or blockers'} match.
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-3">
            <section>
              <div className="mb-1.5 flex items-center gap-2">
                <span
                  className="text-[10px] tracking-wide"
                  style={{ color: attentionCount > 0 ? T.decision : T.muted }}
                >
                  Open
                </span>
                <span className="text-[10px]" style={{ color: T.muted }}>
                  {open.length}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {open.map((concern) => (
                  <ConcernRow
                    key={concern.entity_id}
                    concern={concern}
                    selected={concern.entity_id === selectedId}
                    onSelect={() => openDetail(concern.entity_id)}
                  />
                ))}
              </div>
            </section>

            {closed.length > 0 && (
              <section>
                <div className="mb-1.5 flex items-center gap-2">
                  <span
                    className="text-[10px] tracking-wide"
                    style={{ color: T.muted }}
                  >
                    Recently closed
                  </span>
                  <span className="text-[10px]" style={{ color: T.muted }}>
                    {closed.length}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {closed.map((concern) => (
                    <ConcernRow
                      key={concern.entity_id}
                      concern={concern}
                      selected={concern.entity_id === selectedId}
                      onSelect={() => openDetail(concern.entity_id)}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      <DrawerDetail open={!!selectedId} onClose={closeDetail}>
        {detailLoading ? (
          <Spinner />
        ) : detailError ? (
          <div className="p-6">
            <ErrorBanner>{detailError}</ErrorBanner>
          </div>
        ) : (
          <RiskDetailPanel
            accessToken={accessToken}
            detail={detail}
            onClose={closeDetail}
            onActed={handleActed}
          />
        )}
      </DrawerDetail>
    </div>
  );
};

export default ConcernList;
